import React, { createRef, ReactElement, useEffect, useRef } from 'react';
import { Form, Formik, FormikConfig, FormikErrors, FormikProps, FormikValues } from "formik";
import { FormikStepProps } from "./FormikStep";
import AlertBanner from "../structure/AlertBanner";
import Breadcrumbs from "./Breadcrumbs";
import BackNext from "./BackNext";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { FormikHelpers } from "formik/dist/types";
import { addPageView } from "./PageViewMetric";
import { object, ObjectSchema } from "yup";
import { ApolloClient, useApolloClient } from "@apollo/client";
import ShowHideDiv from "../structure/ShowHideDiv";
import FormViewMetric, {trackFormEvent} from "./FormViewMetric";

interface FormikStepperProps extends Pick<FormikConfig<FormikValues>, 'initialValues'> {
    onSubmit: (values: FormikValues, helpers: FormikHelpers<FormikValues>, navigate: NavigateFunction, apolloClient: ApolloClient<any>) => any;
    children?: React.ReactNode
}

let formikAvailable = false;

export function isFormikAvailable() {
    return formikAvailable;
}

export const StepContext = React.createContext({ steps: [] as ReactElement[], stepChangeCallback: (() => {}) as Function, stepRefs: [] as React.MutableRefObject<any>[] });
export const stepDivId = 'step-div-id';

export function FormikStepper(props: FormikStepperProps) {
    const initialChildrenArray = React.useMemo(() => React.Children.toArray(props.children) as React.ReactElement<FormikStepProps>[], [props.children]);
    const [childrenArray, setChildrenArray] = React.useState(initialChildrenArray);
    const [step, setStep] = React.useState(0);
    const stepRefs = useRef([]);
    const currentChild = childrenArray[step];
    const navigate = useNavigate();
    const formikRef = useRef(null) as React.RefObject<FormikProps<any>>;
    const client = useApolloClient();

    stepRefs.current = childrenArray.map((_, i) => stepRefs.current[i] ?? createRef());

    useEffect(
        () => {
            setActiveStep(0)
            trackFormEvent('formView', formikRef.current?.values.claimId)
            formikAvailable = true;
            return () => {
                formikAvailable = false;
            };
        }, [] // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(() => {
        const formikRefCurrent = formikRef.current;
        const initialValues = formikRefCurrent?.initialValues
        const values = formikRefCurrent?.values
        const filteredChildrenArray = initialChildrenArray.filter((child: ReactElement) => !child.props.hideStep?.(initialValues, values));
        setChildrenArray(filteredChildrenArray)
    }, [initialChildrenArray, formikRef.current?.initialValues, formikRef.current?.values])

    function findStepLabelFor(childrenArray: React.ReactElement<FormikStepProps>[], stepIndex: number) {
        return childrenArray[stepIndex].props.label;
    }

    function setActiveStep(stepIndex: number) {
        setStep(stepIndex);
        formikRef.current?.setFieldValue('currentStep', findStepLabelFor(childrenArray, stepIndex));
        putStepIndexMetric(props, stepIndex);
        formikRef.current?.setErrors({});
    }

    function isSecondToLastStep() {
        return step === childrenArray.length - 2;
    }

    function isLastStep() {
        return step === childrenArray.length - 1;
    }

    function putStepIndexMetric(props: FormikStepperProps, stepIndex: number) {
        addPageView(findStepLabelFor(childrenArray, stepIndex))
    }

    function removeUninitializedFieldsFromValidationFor(validationSchema: ObjectSchema) {
        let newValidationSchema;
        if(validationSchema) {
            const fields = validationSchema.fields!! as any;
            const initialValueKeys = Object.keys(props.initialValues);
            const filteredFields = Object.keys(fields)
                .filter(key => initialValueKeys.includes(key))
                .reduce((obj, key) => {
                    return {
                        ...obj,
                        [key]: fields[key]
                    };
                }, {});
            newValidationSchema = object(filteredFields);
        }
        return newValidationSchema;
    }

    function findStepClassNameFor(stepIndex: number, currentStep: number) {
        if(stepIndex < currentStep) {
            return "prior-step";
        } else if(stepIndex === currentStep) {
            return "current-step";
        }
        return "future-step";
    }

    return (
        <StepContext.Provider value={{ steps: childrenArray, stepChangeCallback: setActiveStep, stepRefs: stepRefs.current }}>
            <Formik {...props}
                    enableReinitialize={false}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validate={currentChild.props.validate}
                    validationSchema={removeUninitializedFieldsFromValidationFor(currentChild.props.validationSchema)}
                    onSubmit={async (values, helpers) => {
                        if (isSecondToLastStep()) {
                            await props.onSubmit(values, helpers, navigate, client);
                        }
                        if (!isLastStep()) {
                            setActiveStep(step + 1);
                        }
                    }}
                    innerRef={formikRef}>
                {(props: FormikProps<FormikValues>) => {
                    const map = new Map(Object.entries(props.errors));
                    let errorArray :ReactElement[] = [];
                    map.forEach((errors) => {
                        if (errors && !((errors as FormikErrors<any>).hidden)) {
                            const errorsArray = errors instanceof Array ? errors : [{ error: errors }];
                            for(const errors of errorsArray) {
                                if(!errors) {
                                    continue;
                                }
                                for (const error of Object.values(errors)) {
                                    errorArray.push(<AlertBanner message={'' + error}/>);
                                }
                            }
                        }
                    });
                    const showBreadcrumbsAndBackButton = step > 0 && !isLastStep();
                    return <Form autoComplete="off">
                        <FormViewMetric/>
                        {showBreadcrumbsAndBackButton &&
                            <Breadcrumbs steps={childrenArray} currentStep={step} stepChangeCallback={setActiveStep}/>}
                        {childrenArray.map((child, index) => {
                            const isActiveIndex = index === step;
                            return <div className={findStepClassNameFor(index, step)}>
                                <ShowHideDiv id={stepDivId} show={isActiveIndex}>
                                    {isActiveIndex && errorArray}
                                    <div ref={stepRefs.current[index]}>
                                        {child}
                                    </div>
                                </ShowHideDiv>
                            </div>;
                        })}
                        <BackNext showSave={showBreadcrumbsAndBackButton}
                                  back={showBreadcrumbsAndBackButton ? () => setActiveStep(step - 1) : undefined}
                                  next={step < childrenArray.length - 2 ? () => {
                                  } : undefined}
                                  {...props}
                        />
                    </Form>
                }}
            </Formik>
        </StepContext.Provider>
    );
}